import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SectionColumn } from '../../types/section.types';
import { useFunnelStore } from '../../store/funnelStore';
import CanvasElement from './CanvasElement';

interface CanvasColumnProps {
  column: SectionColumn;
  sectionId: string;
}

/**
 * Canvas Column Component
 * Renders a column within a section with its elements
 */
const CanvasColumn: React.FC<CanvasColumnProps> = ({ column, sectionId }) => {
  const { currentBreakpoint } = useFunnelStore();

  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: { type: 'column', columnId: column.id, sectionId },
  });

  // Calculate column width based on breakpoint
  const getColumnWidth = () => {
    if (currentBreakpoint === 'mobile' && column.responsive?.mobile) {
      return (column.responsive.mobile / 12) * 100;
    }
    if (currentBreakpoint === 'tablet' && column.responsive?.tablet) {
      return (column.responsive.tablet / 12) * 100;
    }
    return (column.span / 12) * 100;
  };

  const columnWidth = getColumnWidth();

  return (
    <div
      ref={setNodeRef}
      className={`
        canvas-column transition-all duration-200 rounded-lg relative
        ${column.elements.length === 0 
          ? 'min-h-[120px]' 
          : 'min-h-[60px]'
        }
        ${isOver && column.elements.length === 0
          ? 'bg-blue-100 border-2 border-dashed border-blue-500 shadow-inner' 
          : column.elements.length === 0
          ? 'border-2 border-dashed border-gray-300'
          : ''
        }
      `}
      style={{
        flex: `0 0 calc(${columnWidth}% - 16px)`,
        maxWidth: `calc(${columnWidth}% - 16px)`,
      }}
    >
      {column.elements.length === 0 ? (
        // Empty column - show drop zone
        <div className={`
          flex flex-col items-center justify-center h-full text-sm rounded-lg p-6
          transition-all duration-200
          ${isOver 
            ? 'bg-blue-50 text-blue-700' 
            : 'text-gray-400 hover:text-gray-600'
          }
        `}>
          <div className="text-3xl mb-2">
            {isOver ? '⬇️' : '📦'}
          </div>
          <div className="font-medium">
            {isOver ? 'Drop here!' : 'Drop elements here'}
          </div>
          {!isOver && (
            <div className="text-xs mt-1 text-gray-400">
              Drag from right panel
            </div>
          )}
        </div>
      ) : (
        // Column has elements - show them with minimal chrome
        <div className="space-y-3 p-2 relative">
          {column.elements.map((element) => (
            <CanvasElement
              key={element.id}
              element={element}
              sectionId={sectionId}
              columnId={column.id}
            />
          ))}
          
          {/* Only show drop indicator at bottom when actively dragging */}
          {isOver && (
            <div className="h-12 border-2 border-dashed border-blue-500 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 text-xs font-medium animate-pulse">
              ⬇️ Drop to add below
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CanvasColumn;

